// The later steps of signing in and signing up, shared by both pages: the second factor for an account with
// two-factor on (an authenticator code, or a backup code), and the code that verifies a new email address.

import { useTranslation } from '@clavian/i18n/react';
import { Button } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';

import { useApi } from '../api/client';
import { useAuthFailure } from './auth-errors';
import { Failure, TextInput } from './form-controls';

/** "Enter your security code": better-auth's two-factor step after the password (spec/07 §6.6). */
export function SecondFactorForm({ onSignedIn, onBack }: { readonly onSignedIn: () => Promise<void>; readonly onBack: () => void }): ReactNode {
  const { t } = useTranslation('player');
  const api = useApi();
  const failureOf = useAuthFailure();
  const [backup, setBackup] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const form = useForm<{ code: string }>({ defaultValues: { code: '' } });

  const submit = form.handleSubmit(async ({ code }) => {
    setFailure(null);
    try {
      if (backup) await api.verifyTwoFactorBackupCode({ body: { code: code.trim() } });
      else await api.verifyTwoFactorTotp({ body: { code: code.trim() } });
      await onSignedIn();
    } catch (error) {
      setFailure(failureOf(error));
    }
  });

  return (
    <form className="pw-form" key={backup ? 'backup' : 'app'} onSubmit={(event) => void submit(event)} noValidate>
      <p>{backup ? t('signIn.secondFactor.backupHint') : t('signIn.secondFactor.intro')}</p>
      <Failure message={failure} />
      <TextInput
        control={form.control}
        name="code"
        label={backup ? t('signIn.secondFactor.backupCode') : t('signIn.secondFactor.code')}
        inputMode={backup ? 'text' : 'numeric'}
        autoComplete="one-time-code"
        autoFocus
        enterKeyHint="done"
        rules={{ required: t('signIn.required') }}
      />
      <Button type="submit" variant="primary" block busy={form.formState.isSubmitting}>
        {t('signIn.secondFactor.submit')}
      </Button>
      <div className="pw-form__actions">
        <Button
          variant="ghost"
          onClick={() => {
            form.reset({ code: '' });
            setFailure(null);
            setBackup(!backup);
          }}
        >
          {backup ? t('signIn.secondFactor.useApp') : t('signIn.secondFactor.useBackup')}
        </Button>
        <Button variant="ghost" onClick={onBack}>
          {t('signIn.secondFactor.back')}
        </Button>
      </div>
    </form>
  );
}

/** "Check your email": the code that verifies a new address. Verifying does not sign in; `onVerified` does. */
export function VerifyEmailForm({ email, onVerified }: { readonly email: string; readonly onVerified: () => Promise<void> }): ReactNode {
  const { t } = useTranslation('player');
  const api = useApi();
  const failureOf = useAuthFailure();
  const [failure, setFailure] = useState<string | null>(null);
  const [resent, setResent] = useState(false);
  const [resending, setResending] = useState(false);
  const form = useForm<{ code: string }>({ defaultValues: { code: '' } });

  const submit = form.handleSubmit(async ({ code }) => {
    setFailure(null);
    try {
      await api.verifyEmailOTP({ body: { email, otp: code.trim() } });
      await onVerified();
    } catch (error) {
      setFailure(failureOf(error));
    }
  });

  const resend = async () => {
    setResending(true);
    setResent(false);
    try {
      await api.sendEmailVerificationOTP({ body: { email, type: 'email-verification' } });
      setResent(true);
    } catch (error) {
      setFailure(failureOf(error));
    } finally {
      setResending(false);
    }
  };

  return (
    <form className="pw-form" onSubmit={(event) => void submit(event)} noValidate>
      <p role="status">{resent ? t('signIn.verify.resent') : t('signIn.verify.intro', { email })}</p>
      <Failure message={failure} />
      <TextInput
        control={form.control}
        name="code"
        label={t('signIn.verify.code')}
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus
        maxLength={6}
        enterKeyHint="done"
        numeric
        rules={{ required: t('signIn.required') }}
      />
      <Button type="submit" variant="primary" block busy={form.formState.isSubmitting}>
        {t('signIn.verify.submit')}
      </Button>
      <Button variant="ghost" busy={resending} onClick={() => void resend()}>
        {t('signIn.verify.resend')}
      </Button>
    </form>
  );
}
